import { Link } from 'react-router-dom';
import { Github, Linkedin, Rss, X } from 'lucide-react';
import { AuthorLink } from '@/components/AuthorLink';
import { ThemeToggle } from '@/components/ThemeToggle';
import { siteConfig } from '../../site.config';

interface ResourceLink {
  label: string;
  href: string;
  external?: boolean;
}

export function Footer() {
  // Reference: the content you actually use while reading.
  const referenceLinks: ResourceLink[] = [
    { label: 'Glossary', href: '/glossary' },
    { label: 'Reading list', href: '/papers' },
    { label: 'Downloads', href: '/downloads' },
    { label: 'llms.txt', href: '/llms.txt', external: true },
  ];

  // Project: contributing, citing, and the meta pages.
  const projectLinks: ResourceLink[] = [
    { label: 'Contributing', href: '/contributing' },
    { label: 'How to cite', href: '/how-to-cite' },
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
    { label: 'Privacy', href: '/privacy' },
  ];

  const editOnGitHub = siteConfig.repo.baseUrl
    ? { label: 'Edit this page on GitHub', href: siteConfig.repo.baseUrl, external: true }
    : null;
  if (editOnGitHub) {
    projectLinks.unshift(editOnGitHub);
  }

  return (
    <footer className="mt-24 border-t border-rule bg-bg-subtle">
      <div className="container-prose py-12">
        <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="wordmark text-lg text-ink">
              AI<span className="wordmark-accent">Evals</span>
            </div>
            <p className="mt-3 max-w-sm text-sm text-ink-soft">
              The most useful, opinionated reference for AI evals. Built by{' '}
              <AuthorLink className="underline decoration-rule-strong underline-offset-2 transition-colors hover:text-ink" />{' '}
              and contributors.
            </p>
          </div>

          <FooterColumn heading="Reference" links={referenceLinks} />

          <FooterColumn heading="Project" links={projectLinks} />

          <FooterColumn heading="Community" links={buildCommunityLinks()} />
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-rule pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-ink-mute">
            © {siteConfig.copyright.year} {siteConfig.name} ({siteConfig.domain}). Content
            licensed {siteConfig.copyright.contentLicense}. Code under{' '}
            {siteConfig.copyright.codeLicense}.
          </p>
          <div className="flex items-center gap-3">
            {siteConfig.social.github ? (
              <a
                href={siteConfig.social.github}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="GitHub"
                className="inline-flex h-8 w-8 items-center justify-center text-ink-mute transition-colors duration-150 hover:text-ink-soft"
              >
                <Github size={16} strokeWidth={1.75} aria-hidden="true" />
              </a>
            ) : null}
            {siteConfig.social.twitter ? (
              <a
                href={siteConfig.social.twitter}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="X"
                className="inline-flex h-8 w-8 items-center justify-center text-ink-mute transition-colors duration-150 hover:text-ink-soft"
              >
                <X size={16} strokeWidth={1.75} aria-hidden="true" />
              </a>
            ) : null}
            {siteConfig.social.linkedin ? (
              <a
                href={siteConfig.social.linkedin}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="LinkedIn"
                className="inline-flex h-8 w-8 items-center justify-center text-ink-mute transition-colors duration-150 hover:text-ink-soft"
              >
                <Linkedin size={16} strokeWidth={1.75} aria-hidden="true" />
              </a>
            ) : null}
            <a
              href={siteConfig.social.rss}
              aria-label="RSS feed"
              className="inline-flex h-8 w-8 items-center justify-center text-ink-mute transition-colors duration-150 hover:text-ink-soft"
            >
              <Rss size={16} strokeWidth={1.75} aria-hidden="true" />
            </a>
            <div className="ml-1">
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ heading, links }: { heading: string; links: ResourceLink[] }) {
  return (
    <div>
      <h2 className="text-xs font-medium uppercase tracking-wider text-ink-soft">{heading}</h2>
      <ul className="mt-4 flex flex-col gap-2">
        {links.map((link) => (
          <li key={`${heading}:${link.label}`}>
            {link.external ? (
              <a
                href={link.href}
                target="_blank"
                rel="noreferrer noopener"
                className="text-sm text-ink-soft transition-colors hover:text-ink"
              >
                {link.label}
              </a>
            ) : (
              <Link
                to={link.href}
                className="text-sm text-ink-soft transition-colors hover:text-ink"
              >
                {link.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function buildCommunityLinks(): ResourceLink[] {
  const links: ResourceLink[] = [];
  if (siteConfig.social.github) {
    links.push({ label: 'GitHub', href: siteConfig.social.github, external: true });
  }
  links.push({ label: 'RSS', href: siteConfig.social.rss, external: true });
  return links;
}

export default Footer;
